import React, { useMemo } from 'react';
import DateRangeIcon from '@mui/icons-material/DateRange';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalanceRounded';
import ExitToAppRounded from '@mui/icons-material/ExitToAppRounded';
import AddCircleRounded from '@mui/icons-material/AddCircleRounded';
import CreateIcon from '@mui/icons-material/Create';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HelpIcon from '@mui/icons-material/HelpOutline';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import CheckIcon from '@mui/icons-material/Check';
import GroupIcon from '@mui/icons-material/Group';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WarningIcon from '@mui/icons-material/Warning';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import translations from './icons.i18n';
import { useIntl } from 'react-intl';
import { CloudUpload, SubdirectoryArrowLeft } from '@mui/icons-material';
import classNames from 'classnames';
import * as css from './icon.module.scss';

export enum IconType {
  OpenRow,
  OpenRowTriangle,
  CloseRowTriangle,
  CloseRow,
  EndNode,
  CloudUpload,
  Dashboard,
  DateRange,
  Settings,
  AccountBalance,
  ExitToAppRounded,
  AddCircleRounded,
  Info,
  Create,
  Edit,
  Delete,
  ArrowDropDown,
  CheckCircle,
  CheckCircleOutline,
  RadioButtonUnchecked,
  Help,
  PowerSettingsNewIcon,
  Alert,
  PersonAddDisabledIcon,
  Search,
  Clear,
  CheckBox,
  CheckBoxOutline,
  IndeterminateCheckBox,
  Check,
  SubdirectoryArrowLeft,
  Group,
  FileUpload,
  FileUploadActive,
  FileUploaded,
  PDFFileUploaded,
  PDFFile,
  PDFFileOpaque,
  GenericFile,
  GenericFileOpaque,
  LocationOn,
  ArrowBoxLeft,
  ArrowBoxRight,
  Warning,
  OpenInNew,
  Evaluation,
}

interface InnerIconInterface {
  htmlId: string;
  title?: string;
  fill?: string;
  className?: string;
}

export interface IconInterface extends Omit<InnerIconInterface, 'htmlId'> {
  type: IconType;
  htmlId?: string;
}

const Icon: React.FC<React.PropsWithChildren<IconInterface>> = ({
  type,
  title,
  htmlId = '',
  ...iconProps
}) => {
  const props = { ...iconProps, htmlId }; // htmlId has differentiating types that must be handled
  const { className } = props;
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      alert: formatMessage({ id: translations.alert.id }),
      open: formatMessage({ id: translations.open.id }),
      close: formatMessage({ id: translations.close.id }),
      upload: formatMessage({ id: translations.upload.id }),
      endnode: formatMessage({ id: translations.endnode.id }),
      dashboard: formatMessage({ id: translations.dashboard.id }),
      dateRange: formatMessage({ id: translations.dateRange.id }),
      settings: formatMessage({ id: translations.settings.id }),
      accountBalance: formatMessage({ id: translations.accountBalance.id }),
      exitToAppRounded: formatMessage({ id: translations.exitToAppRounded.id }),
      addCircleRounded: formatMessage({ id: translations.addCircleRounded.id }),
      info: formatMessage({ id: translations.info.id }),
      edit: formatMessage({ id: translations.edit.id }),
      delete: formatMessage({ id: translations.delete.id }),
      arrowDropDown: formatMessage({ id: translations.arrowDropDown.id }),
      checkCircle: formatMessage({ id: translations.checkCircle.id }),
      checkCircleOutline: formatMessage({
        id: translations.checkCircleOutline.id,
      }),
      radioButtonUnchecked: formatMessage({
        id: translations.radioButtonUnchecked.id,
      }),
      help: formatMessage({ id: translations.help.id }),
      search: formatMessage({ id: translations.search.id }),
      clear: formatMessage({ id: translations.clear.id }),
      checkBox: formatMessage({ id: translations.checkBox.id }),
      checkBoxOutline: formatMessage({ id: translations.checkBoxOutline.id }),
      check: formatMessage({ id: translations.check.id }),
      group: formatMessage({ id: translations.group.id }),
      fileUpload: formatMessage({ id: translations.fileUpload.id }),
      fileUploaded: formatMessage({ id: translations.fileUploaded.id }),
      pdf: formatMessage({ id: translations.pdfFile.id }),
      locationOn: formatMessage({ id: translations.locationOn.id }),
      move: formatMessage({ id: translations.move.id }),
      warning: formatMessage({ id: translations.warning.id }),
      openInNew: formatMessage({ id: translations.openInNew.id }),
      evaluation: formatMessage({ id: translations.evaluation.id }),
      indeterminateCheckBox: formatMessage({
        id: translations.indeterminateCheckBox.id,
      }),
    }),
    [formatMessage],
  );

  switch (type) {
    case IconType.OpenRow:
      return (
        <OpenCircleIcon
          title={title === undefined ? labels.open : title}
          {...props}
        />
      );
    case IconType.CloudUpload:
      return (
        <CloudUpload
          className={className}
          titleAccess={title === undefined ? labels.upload : title}
        />
      );
    case IconType.OpenRowTriangle:
      return (
        <OpenIcon
          title={title === undefined ? labels.open : title}
          {...props}
        />
      );
    case IconType.CloseRowTriangle:
      return (
        <CloseIcon
          title={title === undefined ? labels.close : title}
          {...props}
        />
      );
    case IconType.CloseRow:
      return (
        <CloseCircleIcon
          title={title === undefined ? labels.close : title}
          {...props}
        />
      );
    case IconType.EndNode:
      return (
        <EndNodeIcon
          title={title === undefined ? labels.endnode : title}
          {...props}
        />
      );
    case IconType.Dashboard:
      return (
        <DashboardIcon
          title={title === undefined ? labels.dashboard : title}
          {...props}
        />
      );
    case IconType.DateRange:
      return (
        <DateRangeIcon
          className={className}
          titleAccess={title === undefined ? labels.dateRange : title}
        />
      );
    case IconType.Settings:
      return (
        <SettingsIcon
          className={className}
          titleAccess={title === undefined ? labels.settings : title}
        />
      );
    case IconType.AccountBalance:
      return (
        <AccountBalanceIcon
          className={className}
          titleAccess={title === undefined ? labels.accountBalance : title}
        />
      );
    case IconType.ExitToAppRounded:
      return (
        <ExitToAppRounded
          className={className}
          titleAccess={title === undefined ? labels.exitToAppRounded : title}
        />
      );
    case IconType.AddCircleRounded:
      return (
        <AddCircleRounded
          className={className}
          titleAccess={title === undefined ? labels.addCircleRounded : title}
        />
      );
    case IconType.Info:
      return (
        <InfoIcon
          className={className}
          titleAccess={title === undefined ? labels.info : title}
        />
      );
    case IconType.Create:
      return <CreateIcon className={className} />;
    case IconType.Edit:
      return (
        <EditIcon
          className={className}
          titleAccess={title === undefined ? labels.edit : title}
        />
      );
    case IconType.Delete:
      return (
        <DeleteIcon
          className={className}
          titleAccess={title === undefined ? labels.delete : title}
        />
      );
    case IconType.ArrowDropDown:
      return (
        <ArrowDropDownIcon
          className={className}
          titleAccess={title === undefined ? labels.arrowDropDown : title}
        />
      );
    case IconType.CheckCircle:
      return (
        <CheckCircleIcon
          className={className}
          titleAccess={title === undefined ? labels.checkCircle : title}
        />
      );
    case IconType.CheckCircleOutline:
      return (
        <CheckCircleOutlineIcon
          className={className}
          titleAccess={title === undefined ? labels.checkCircleOutline : title}
        />
      );
    case IconType.RadioButtonUnchecked:
      return (
        <RadioButtonUncheckedIcon
          className={className}
          titleAccess={
            title === undefined ? labels.radioButtonUnchecked : title
          }
        />
      );
    case IconType.Help:
      return (
        <HelpIcon
          className={className}
          titleAccess={title === undefined ? labels.help : title}
        />
      );
    case IconType.PowerSettingsNewIcon:
      return <PowerSettingsNewIcon className={className} />;
    case IconType.Alert:
      return (
        <ErrorOutlineIcon
          className={className}
          titleAccess={title === undefined ? labels.alert : title}
        />
      );
    case IconType.PersonAddDisabledIcon:
      return <PersonAddDisabledIcon className={className} />;
    case IconType.Search:
      return (
        <SearchIcon
          className={className}
          titleAccess={title === undefined ? labels.search : title}
        />
      );
    case IconType.Clear:
      return (
        <ClearIcon
          className={className}
          titleAccess={title === undefined ? labels.clear : title}
        />
      );
    case IconType.CheckBox:
      return (
        <CheckBoxIcon
          className={className}
          titleAccess={title === undefined ? labels.checkBox : title}
        />
      );
    case IconType.CheckBoxOutline:
      return (
        <CheckBoxOutlineBlankIcon
          className={className}
          titleAccess={title === undefined ? labels.checkBoxOutline : title}
        />
      );
    case IconType.IndeterminateCheckBox:
      return (
        <IndeterminateCheckBoxIcon
          className={className}
          titleAccess={
            title === undefined ? labels.indeterminateCheckBox : title
          }
        />
      );
    case IconType.SubdirectoryArrowLeft:
      return <SubdirectoryArrowLeft className={className} />;
    case IconType.Check:
      return (
        <CheckIcon
          className={className}
          titleAccess={title === undefined ? labels.check : title}
        />
      );
    case IconType.Group:
      return (
        <GroupIcon
          className={className}
          titleAccess={title === undefined ? labels.group : title}
        />
      );
    case IconType.FileUpload:
      return (
        <FileUploadCloudIcon
          title={title === undefined ? labels.fileUpload : title}
          {...props}
        />
      );
    case IconType.FileUploadActive:
      return (
        <FileUploadCloudIcon
          title={title === undefined ? labels.fileUpload : title}
          {...props}
          active
        />
      );
    case IconType.FileUploaded:
      return (
        <GenericFileUploadedCloudIcon
          title={title === undefined ? labels.fileUploaded : title}
          {...props}
        />
      );
    case IconType.PDFFileUploaded:
      return (
        <PDFFileUploadedCloudIcon
          title={title === undefined ? labels.fileUploaded : title}
          {...props}
        />
      );
    case IconType.PDFFile:
      return (
        <PDFFileIcon
          title={title === undefined ? labels.pdf : title}
          {...props}
        />
      );
    case IconType.PDFFileOpaque:
      return (
        <PDFFileIcon
          title={title === undefined ? labels.pdf : title}
          {...props}
          opaque
        />
      );
    case IconType.GenericFile:
      return (
        <GenericFileIcon
          title={title === undefined ? labels.pdf : title}
          {...props}
        />
      );
    case IconType.GenericFileOpaque:
      return (
        <GenericFileIcon
          title={title === undefined ? labels.pdf : title}
          {...props}
          opaque
        />
      );
    case IconType.LocationOn:
      return (
        <LocationOnIcon
          className={className}
          titleAccess={title === undefined ? labels.locationOn : title}
        />
      );
    case IconType.ArrowBoxLeft:
      return (
        <ArrowBoxLeftSvg
          title={title === undefined ? labels.move : title}
          {...props}
        />
      );
    case IconType.ArrowBoxRight:
      return (
        <ArrowBoxRightSvg
          title={title === undefined ? labels.move : title}
          {...props}
        />
      );
    case IconType.Warning:
      return (
        <WarningIcon
          id={htmlId}
          className={classNames(className, css.warningIcon)}
          {...iconProps}
        />
      );
    case IconType.OpenInNew:
      return (
        <OpenInNewIcon
          id={htmlId}
          titleAccess={title === undefined ? labels.openInNew : title}
          {...iconProps}
        />
      );
    case IconType.Evaluation:
      return (
        <EvaluationSvg
          title={title === undefined ? labels.evaluation : title}
          {...props}
        />
      );
  }
};

const OpenCircleIcon: React.FC<React.PropsWithChildren<InnerIconInterface>> = ({
  htmlId,
  title,
  className,
  fill,
}) => {
  const titleId = `openCircleIconTitle-${htmlId}`;
  return (
    <svg
      className={classNames(css.commonIcon, css.alpIcon, className)}
      xmlns="http://www.w3.org/2000/svg"
      aria-labelledby={titleId}
      role={title ? 'img' : ''}
      aria-hidden={title === ''}
      viewBox="0 0 24 24"
    >
      <title id={titleId}>{title}</title>
      <g transform="matrix(1 0 0 -1 0 24)" fillRule="evenodd">
        <circle fill="#E0E4E4" cx="12.256" cy="12" r="12" />
        <path fillOpacity=".85" fill={fill} d="M8.233 14l4.012-5 4.011 5z" />
      </g>
    </svg>
  );
};

const OpenIcon: React.FC<React.PropsWithChildren<InnerIconInterface>> = ({
  htmlId,
  title,
  className,
  fill,
}) => {
  const titleId = `openIconTitle-${htmlId}`;
  return (
    <svg
      className={classNames(css.commonIcon, css.alpIcon, className)}
      xmlns="http://www.w3.org/2000/svg"
      aria-labelledby={titleId}
      role={title ? 'img' : ''}
      aria-hidden={title === ''}
      viewBox="0 0 24 24"
    >
      <title id={titleId}>{title}</title>
      <path
        fill={fill}
        fillOpacity=".85"
        d="M8.233 10l4.012 5 4.011-5z"
        fillRule="evenodd"
      />
    </svg>
  );
};

const CloseCircleIcon: React.FC<
  React.PropsWithChildren<InnerIconInterface>
> = ({ htmlId, title, className, fill }) => {
  const titleId = `closeCircleIconTitle-${htmlId}`;
  return (
    <svg
      className={classNames(css.commonIcon, css.alpIcon, className)}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-labelledby={titleId}
      role={title ? 'img' : ''}
      aria-hidden={title === ''}
    >
      <title id={titleId}>{title}</title>
      <g transform="matrix(-1 0 0 1 24 0)" fillRule="evenodd">
        <circle cx="12.256" cy="12" r="12" fill="#E0E4E4" />
        <path fill={fill} fillOpacity=".85" d="M8.233 14l4.012-5 4.011 5z" />
      </g>
    </svg>
  );
};

const CloseIcon: React.FC<React.PropsWithChildren<InnerIconInterface>> = ({
  htmlId,
  title,
  className,
  fill,
}) => {
  const titleId = `closeIconTitle-${htmlId}`;
  return (
    <svg
      className={classNames(css.commonIcon, css.alpIcon, className)}
      id={title}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-labelledby={titleId}
      role={title ? 'img' : ''}
      aria-hidden={title === ''}
    >
      <title id={titleId}>{title}</title>
      <path fill={fill} fillOpacity=".85" d="M8.233 14l4.012-5 4.011 5z" />
    </svg>
  );
};

const DashboardIcon: React.FC<React.PropsWithChildren<InnerIconInterface>> = ({
  htmlId,
  title,
  className,
}) => {
  const titleId = `dashboardIconTitle-${htmlId}`;
  return (
    <svg
      className={classNames(css.commonIcon, css.alpIcon, className)}
      viewBox="0 0 26 15"
      xmlns="http://www.w3.org/2000/svg"
      aria-labelledby={titleId}
      role={title ? 'img' : ''}
      aria-hidden={title === ''}
    >
      <title id={titleId}>{title}</title>
      <g fill="rgba(0, 0, 0, 0.54)" fillRule="evenodd">
        <path d="M10.432 11.033c-.668 1.017-.407 2.41.638 3.078 1.016.668 2.41.406 3.078-.639.667-1.016 5.022-9.406 3.977-10.074-1.016-.668-7.026 6.62-7.693 7.635zM20.527 4.465a1.153 1.153 0 00-.116 1.626 10.157 10.157 0 012.496 6.649c0 .638.523 1.16 1.162 1.16.638 0 1.161-.522 1.161-1.16 0-2.99-1.103-5.894-3.077-8.159a1.176 1.176 0 00-1.626-.116zM1.161 13.616c.639 0 1.162-.522 1.162-1.161 0-5.574 4.529-10.103 10.103-10.103 1.016 0 2.003.145 2.961.435a1.169 1.169 0 001.452-.784 1.169 1.169 0 00-.784-1.451C14.865.203 13.645 0 12.425 0 5.576 0 0 5.574 0 12.426c0 .668.523 1.19 1.161 1.19z" />
      </g>
    </svg>
  );
};

const EndNodeIcon: React.FC<React.PropsWithChildren<InnerIconInterface>> = ({
  htmlId,
  title,
  className,
}) => {
  const titleId = `endnodeIconTitle-${htmlId}`;
  return (
    <svg
      className={classNames(css.commonIcon, css.alpIcon, className)}
      viewBox="0 0 18 16"
      aria-labelledby={titleId}
      role={title ? 'img' : ''}
      aria-hidden={title === ''}
    >
      <title id={titleId}>{title}</title>
      <g
        id="PSA-UHA-Activitymodal"
        stroke="none"
        strokeWidth="1"
        fill="none"
        fillRule="evenodd"
        opacity="0.5"
      >
        <g
          id="ALP-Activitymodal---Grids-and-elements"
          transform="translate(-246.000000, -1194.000000)"
          fill="#444444"
          fillRule="nonzero"
        >
          <g id="Endnode" transform="translate(246.000000, 1194.000000)">
            <path
              d="M6,0 L6,4.44444444 L0,4.44444444 L0,16 L2,16 L2,10.6666667 L5,10.6666667 L5,16 L6,16 L18,16 L18,0 L6,0 Z M5,8.88888889 L2,8.88888889 L2,6.22222222 L5,6.22222222 L5,8.88888889 Z M11,13.3333333 L8,13.3333333 L8,10.6666667 L11,10.6666667 L11,13.3333333 Z M11,8.88888889 L8,8.88888889 L8,6.22222222 L11,6.22222222 L11,8.88888889 Z M11,4.44444444 L8,4.44444444 L8,1.77777778 L11,1.77777778 L11,4.44444444 Z M16,13.3333333 L13,13.3333333 L13,10.6666667 L16,10.6666667 L16,13.3333333 Z M16,8.88888889 L13,8.88888889 L13,6.22222222 L16,6.22222222 L16,8.88888889 Z M5,13.3333333 L5,16 L2,16 L2,13.3333333 L5,13.3333333 Z M16,1.77777778 L16,4.44444444 L13,4.44444444 L13,1.77777778 L16,1.77777778 Z"
              id="Shape"
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

interface FileUploadIconInterface extends InnerIconInterface {
  active?: boolean;
}

const FileUploadCloudIcon: React.FC<
  React.PropsWithChildren<FileUploadIconInterface>
> = ({ htmlId, title, className, fill, active }) => {
  const titleId = `FileUploadCloudIcon-${htmlId}`;
  return (
    <svg
      className={classNames(css.commonIcon, className)}
      id={title}
      viewBox="0 0 193 119"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-labelledby={titleId}
      role={title ? 'img' : ''}
      aria-hidden={title === ''}
    >
      <title id={titleId}>{title}</title>
      <path
        fill={fill || active ? 'white' : '#0F6A88'}
        d="M37.4662 119H155.531C176.224 119 193 102.091 193 81.2341C193 62.6374 179.655 47.216 162.097 44.0821C157.347 30.0564 144.189 19.9533 128.664 19.9533C123.127 19.9533 117.91 21.2731 113.245 23.5586C106.075 9.57656 91.6285 0 74.9371 0C51.1215 0 31.8112 19.4612 31.8112 43.4659C31.8112 43.6268 31.834 43.7786 31.834 43.9395C13.8217 46.6849 2.3262e-07 62.3109 2.3262e-07 81.2341C-0.00228096 102.093 16.7736 119 37.4662 119Z"
      />
      <path
        d="M59 69.826L94.536 33L129.88 70.011L59 69.826Z"
        fill={active ? '#0F6A88' : 'white'}
      />
      <path
        d="M79.6362 106.562L109.886 106.604L109.957 56.354L79.7066 56.3117L79.6362 106.562Z"
        fill={active ? '#0F6A88' : 'white'}
      />
    </svg>
  );
};

const GenericFileUploadedCloudIcon: React.FC<
  React.PropsWithChildren<InnerIconInterface>
> = ({ htmlId, title, className, fill }) => {
  const titleId = `GenericFileUploadedCloudIcon-${htmlId}`;
  return (
    <svg
      className={classNames(css.commonIcon, className)}
      id={title}
      viewBox="0 0 193 119"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-labelledby={titleId}
      role={title ? 'img' : ''}
      aria-hidden={title === ''}
    >
      <title id={titleId}>{title}</title>
      <path
        fill={fill || '#0F6A88'}
        d="M37.4662 119H155.531C176.224 119 193 102.091 193 81.2341C193 62.6374 179.655 47.216 162.097 44.0821C157.347 30.0564 144.189 19.9533 128.664 19.9533C123.127 19.9533 117.91 21.2731 113.245 23.5586C106.075 9.57656 91.6285 0 74.9371 0C51.1215 0 31.8112 19.4612 31.8112 43.4659C31.8112 43.6268 31.834 43.7786 31.834 43.9395C13.8217 46.6849 2.3262e-07 62.3109 2.3262e-07 81.2341C-0.00228096 102.093 16.7736 119 37.4662 119Z"
      />
      <path
        d="M109.921 30.6572H71.8177C69.4825 30.6572 67.585 32.5498 67.585 34.8908V102.158C67.585 104.497 69.4825 106.392 71.8177 106.392H122.621C124.962 106.392 126.854 104.497 126.854 102.158V47.5915L109.921 30.6572ZM109.921 36.6428L120.869 47.5915H109.921V36.6428ZM122.622 102.158H71.8177V34.8908H105.687V47.5915C105.687 49.93 107.58 51.8251 109.92 51.8251H122.621V102.158H122.622Z"
        fill="white"
      />
    </svg>
  );
};

const PDFFileUploadedCloudIcon: React.FC<
  React.PropsWithChildren<InnerIconInterface>
> = ({ htmlId, title, className, fill }) => {
  const titleId = `PDFFileUploadedCloudIcon-${htmlId}`;
  return (
    <svg
      className={classNames(css.commonIcon, className)}
      id={title}
      viewBox="0 0 193 119"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-labelledby={titleId}
      role={title ? 'img' : ''}
      aria-hidden={title === ''}
    >
      <title id={titleId}>{title}</title>
      <path
        fill={fill || '#0F6A88'}
        d="M37.4662 119H155.531C176.224 119 193 102.091 193 81.2341C193 62.6374 179.655 47.216 162.097 44.0821C157.347 30.0564 144.189 19.9533 128.664 19.9533C123.127 19.9533 117.91 21.2731 113.245 23.5586C106.075 9.57656 91.6285 0 74.9371 0C51.1215 0 31.8112 19.4612 31.8112 43.4659C31.8112 43.6268 31.834 43.7786 31.834 43.9395C13.8217 46.6849 2.3262e-07 62.3109 2.3262e-07 81.2341C-0.00228096 102.093 16.7736 119 37.4662 119Z"
      />
      <path
        d="M103.679 65.0602C102.096 63.2956 100.533 61.324 99.1252 59.5501C98.4612 58.712 97.8259 57.9125 97.2318 57.1863L97.188 57.1333C98.0582 54.6551 98.5555 52.6271 98.6666 51.104C98.9485 47.2238 98.5151 44.7246 97.3429 43.4649C96.551 42.6124 95.362 42.3154 94.2436 42.6915C93.4425 42.96 92.3579 43.6795 91.7377 45.5796C90.8137 48.412 91.2614 53.4282 93.8885 57.5768C92.7163 60.6466 91.0821 64.1741 89.2771 67.5325C85.8396 68.7375 83.0997 70.3221 81.1331 72.2441C78.564 74.7517 77.5197 77.2409 78.267 79.072C78.7273 80.2063 79.7918 80.8829 81.1138 80.8829C82.0344 80.8829 83.0299 80.5505 83.9934 79.9227C86.4261 78.3323 89.6028 73.0334 91.3068 69.9628C94.8319 68.8621 98.3123 68.4119 100.081 68.2411C100.881 68.1636 101.676 68.1022 102.441 68.0593C105.537 71.3285 108.069 73.0527 110.401 73.4827C110.871 73.5703 111.34 73.614 111.799 73.614C113.7 73.614 115.271 72.8558 116.004 71.586C116.557 70.6267 116.546 69.5075 115.973 68.5145C114.679 66.2736 110.772 65.04 104.974 65.04C104.556 65.0409 104.125 65.0467 103.679 65.0602ZM82.3794 77.4571C81.9191 77.7575 81.4462 77.9368 81.1129 77.9368C81.0498 77.9368 81.0069 77.93 80.9825 77.9242C80.9244 77.6305 81.1331 76.3607 83.1906 74.3503C84.1566 73.4062 85.3785 72.5445 86.825 71.7821C84.9998 74.7189 83.3572 76.8184 82.3794 77.4571ZM94.5365 46.4943C94.7511 45.8371 95.017 45.54 95.1828 45.4845C95.187 45.4836 95.1903 45.4819 95.1937 45.4811C95.3536 45.6612 96.0335 46.6903 95.7289 50.8928C95.6759 51.6257 95.4975 52.5522 95.1971 53.6529C94.2142 51.1217 93.9441 48.3094 94.5365 46.4943ZM99.7967 65.311C98.2298 65.4616 95.8433 65.7721 93.1808 66.4024C94.2134 64.3407 95.1693 62.2513 95.9679 60.315C96.2447 60.6617 96.5283 61.0177 96.8161 61.382C97.7796 62.5963 98.8618 63.9612 99.9835 65.2941L99.7967 65.311ZM113.42 69.988C113.463 70.0637 113.463 70.094 113.452 70.1142C113.328 70.3288 112.768 70.6696 111.795 70.6696C111.523 70.6696 111.233 70.6418 110.935 70.5863C109.72 70.3616 108.303 69.5218 106.614 68.024C111.196 68.2427 113.049 69.3476 113.42 69.988Z"
        fill="white"
      />
      <path
        d="M109.921 30.6572H71.8177C69.4825 30.6572 67.585 32.5498 67.585 34.8908V102.158C67.585 104.497 69.4825 106.392 71.8177 106.392H122.621C124.962 106.392 126.854 104.497 126.854 102.158V47.5915L109.921 30.6572ZM109.921 36.6428L120.869 47.5915H109.921V36.6428ZM122.622 102.158H71.8177V34.8908H105.687V47.5915C105.687 49.93 107.58 51.8251 109.92 51.8251H122.621V102.158H122.622Z"
        fill="white"
      />
      <path
        d="M90.2251 84.8119C89.8793 84.5065 89.4644 84.2843 88.9932 84.1539C88.5278 84.0209 87.8614 83.9536 87.0123 83.9536H84.1941C83.6581 83.9536 83.2542 84.0765 82.9925 84.3214C82.7283 84.5688 82.5928 84.9693 82.5928 85.5104V93.8681C82.5928 94.3545 82.7131 94.7332 82.9504 94.9957C83.1919 95.265 83.5134 95.4022 83.9055 95.4022C84.2808 95.4022 84.5972 95.265 84.8446 94.994C85.087 94.7281 85.2107 94.3444 85.2107 93.8538V90.9953H87.0123C88.4033 90.9953 89.472 90.6915 90.1864 90.0915C90.9135 89.4839 91.2829 88.5852 91.2829 87.4197C91.2829 86.877 91.1937 86.3805 91.0162 85.9429C90.8378 85.4994 90.5718 85.1199 90.2251 84.8119ZM88.4041 88.3067C88.2468 88.5187 88.0221 88.6677 87.7158 88.7636C87.3901 88.8646 86.9753 88.9168 86.4805 88.9168H85.2115V86.0237H86.4805C87.6241 86.0237 88.0877 86.2517 88.272 86.4377C88.5178 86.7003 88.6372 87.036 88.6372 87.4627C88.6372 87.8144 88.559 88.098 88.4041 88.3067Z"
        fill="white"
      />
      <path
        d="M100.636 84.9885C100.179 84.5888 99.6621 84.312 99.1 84.1664C98.553 84.025 97.8899 83.9526 97.1317 83.9526H94.2698C93.7405 83.9526 93.345 84.0797 93.0934 84.3322C92.8418 84.5846 92.7139 84.9801 92.7139 85.5086V93.459C92.7139 93.8318 92.7467 94.1305 92.814 94.3704C92.8906 94.643 93.0555 94.8559 93.3038 95.0032C93.5402 95.1462 93.8718 95.2152 94.3144 95.2152H97.1763C97.6829 95.2152 98.1457 95.1824 98.5522 95.1168C98.9662 95.0494 99.3592 94.9316 99.7193 94.7675C100.083 94.6018 100.421 94.3779 100.728 94.1019C101.113 93.7485 101.433 93.3421 101.681 92.8919C101.926 92.4433 102.11 91.9342 102.226 91.3805C102.342 90.8319 102.401 90.2167 102.401 89.5545C102.4 87.5282 101.805 85.9916 100.636 84.9885ZM98.723 92.5931C98.5808 92.7168 98.41 92.8161 98.2147 92.886C98.0102 92.9583 97.8117 93.0046 97.6223 93.0223C97.4229 93.0408 97.141 93.0509 96.7842 93.0509H95.3301V86.1119H96.5696C97.2184 86.1119 97.7746 86.1826 98.224 86.3215C98.6439 86.4494 99.0057 86.7599 99.2994 87.2463C99.6007 87.7436 99.7538 88.5152 99.7538 89.541C99.7538 90.9875 99.408 92.0133 98.723 92.5931Z"
        fill="white"
      />
      <path
        d="M110.599 83.9536H105.422C105.077 83.9536 104.798 84.0049 104.569 84.1101C104.324 84.2212 104.141 84.4021 104.025 84.6487C103.917 84.8793 103.865 85.1612 103.865 85.5104V93.853C103.865 94.3554 103.988 94.7416 104.228 95.0008C104.473 95.2667 104.793 95.4022 105.178 95.4022C105.556 95.4022 105.873 95.2692 106.12 95.0058C106.361 94.7458 106.483 94.3579 106.483 93.853V90.4912H109.89C110.271 90.4912 110.569 90.3995 110.776 90.2169C110.99 90.0275 111.099 89.7734 111.099 89.4604C111.099 89.1482 110.993 88.8932 110.782 88.703C110.578 88.5162 110.277 88.422 109.89 88.422H106.483V86.0691H110.6C111.003 86.0691 111.313 85.9724 111.523 85.7805C111.737 85.5853 111.846 85.3253 111.846 85.008C111.846 84.6975 111.737 84.44 111.522 84.2414C111.31 84.0495 111 83.9536 110.599 83.9536Z"
        fill="white"
      />
    </svg>
  );
};

interface FileIconInterface extends InnerIconInterface {
  opaque?: boolean;
}

const PDFFileIcon: React.FC<React.PropsWithChildren<FileIconInterface>> = ({
  htmlId,
  title,
  className,
  fill,
  opaque,
}) => {
  const titleId = `PDFFileIcon-${htmlId}`;
  return (
    <svg
      className={classNames(css.commonIcon, className)}
      id={title}
      viewBox="0 0 65 82"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-labelledby={titleId}
      role={title ? 'img' : ''}
      aria-hidden={title === ''}
    >
      <title id={titleId}>{title}</title>
      {opaque && <rect width="54" height="70" x={4} y={4} fill="white" />}
      <path
        fill={fill || '#0F6A88'}
        d="M36.6793 35.0602C35.0965 33.2956 33.533 31.324 32.1252 29.5501C31.4612 28.712 30.8259 27.9125 30.2318 27.1863L30.188 27.1333C31.0582 24.6551 31.5555 22.6271 31.6666 21.104C31.9485 17.2238 31.5151 14.7246 30.3429 13.4649C29.551 12.6124 28.362 12.3154 27.2436 12.6915C26.4425 12.96 25.3579 13.6795 24.7377 15.5796C23.8137 18.412 24.2614 23.4282 26.8885 27.5768C25.7163 30.6466 24.0821 34.1741 22.2771 37.5325C18.8396 38.7375 16.0997 40.3221 14.1331 42.2441C11.564 44.7517 10.5197 47.2409 11.267 49.072C11.7273 50.2063 12.7918 50.8829 14.1138 50.8829C15.0344 50.8829 16.0299 50.5505 16.9934 49.9227C19.4261 48.3323 22.6028 43.0334 24.3068 39.9628C27.8319 38.8621 31.3123 38.4119 33.0811 38.2411C33.8814 38.1636 34.6757 38.1022 35.4407 38.0593C38.5365 41.3285 41.0686 43.0527 43.4012 43.4827C43.8708 43.5703 44.3403 43.614 44.7989 43.614C46.6999 43.614 48.271 42.8558 49.0039 41.586C49.5568 40.6267 49.5458 39.5075 48.9728 38.5145C47.6785 36.2736 43.7723 35.04 37.9736 35.04C37.5562 35.0409 37.1253 35.0467 36.6793 35.0602ZM15.3794 47.4571C14.9191 47.7575 14.4462 47.9368 14.1129 47.9368C14.0498 47.9368 14.0069 47.93 13.9825 47.9242C13.9244 47.6305 14.1331 46.3607 16.1906 44.3503C17.1566 43.4062 18.3785 42.5445 19.825 41.7821C17.9998 44.7189 16.3572 46.8184 15.3794 47.4571ZM27.5365 16.4943C27.7511 15.8371 28.017 15.54 28.1828 15.4845C28.187 15.4836 28.1903 15.4819 28.1937 15.4811C28.3536 15.6612 29.0335 16.6903 28.7289 20.8928C28.6759 21.6257 28.4975 22.5522 28.1971 23.6529C27.2142 21.1217 26.9441 18.3094 27.5365 16.4943ZM32.7967 35.311C31.2298 35.4616 28.8433 35.7721 26.1808 36.4024C27.2134 34.3407 28.1693 32.2513 28.9679 30.315C29.2447 30.6617 29.5283 31.0177 29.8161 31.382C30.7796 32.5963 31.8618 33.9612 32.9835 35.2941L32.7967 35.311ZM46.4197 39.988C46.4634 40.0637 46.4626 40.094 46.4516 40.1142C46.3279 40.3288 45.7683 40.6696 44.7947 40.6696C44.5229 40.6696 44.2335 40.6418 43.9347 40.5863C42.7196 40.3616 41.3025 39.5218 39.6136 38.024C44.1964 38.2427 46.0486 39.3476 46.4197 39.988Z"
      />
      <path
        fill={fill || '#0F6A88'}
        d="M42.9206 0.657227H4.81769C2.48253 0.657227 0.584961 2.54975 0.584961 4.89079V72.1583C0.584961 74.4968 2.48253 76.3919 4.81769 76.3919H55.6213C57.9615 76.3919 59.8541 74.4968 59.8541 72.1583V17.5915L42.9206 0.657227ZM42.9206 6.64279L53.8693 17.5915H42.9206V6.64279ZM55.6222 72.1583H4.81769V4.89079H38.6871V17.5915C38.6871 19.93 40.5804 21.8251 42.9198 21.8251H55.6213V72.1583H55.6222Z"
      />
      <path
        fill={fill || '#0F6A88'}
        d="M23.2251 54.8119C22.8793 54.5065 22.4644 54.2843 21.9932 54.1539C21.5278 54.0209 20.8614 53.9536 20.0123 53.9536H17.1941C16.6581 53.9536 16.2542 54.0765 15.9925 54.3214C15.7283 54.5688 15.5928 54.9693 15.5928 55.5104V63.8681C15.5928 64.3545 15.7131 64.7332 15.9504 64.9957C16.1919 65.265 16.5134 65.4022 16.9055 65.4022C17.2808 65.4022 17.5972 65.265 17.8446 64.994C18.087 64.7281 18.2107 64.3444 18.2107 63.8538V60.9953H20.0123C21.4033 60.9953 22.472 60.6915 23.1864 60.0915C23.9135 59.4839 24.2829 58.5852 24.2829 57.4197C24.2829 56.877 24.1937 56.3805 24.0162 55.9429C23.8378 55.4994 23.5718 55.1199 23.2251 54.8119ZM21.4041 58.3067C21.2468 58.5187 21.0221 58.6677 20.7158 58.7636C20.3901 58.8646 19.9753 58.9168 19.4805 58.9168H18.2115V56.0237H19.4805C20.6241 56.0237 21.0877 56.2517 21.272 56.4377C21.5178 56.7003 21.6372 57.036 21.6372 57.4627C21.6372 57.8144 21.559 58.098 21.4041 58.3067Z"
      />
      <path
        fill={fill || '#0F6A88'}
        d="M33.6357 54.9885C33.1788 54.5888 32.6621 54.312 32.1 54.1664C31.553 54.025 30.8899 53.9526 30.1317 53.9526H27.2698C26.7405 53.9526 26.345 54.0797 26.0934 54.3322C25.8418 54.5846 25.7139 54.9801 25.7139 55.5086V63.459C25.7139 63.8318 25.7467 64.1305 25.814 64.3704C25.8906 64.643 26.0555 64.8559 26.3038 65.0032C26.5402 65.1462 26.8718 65.2152 27.3144 65.2152H30.1763C30.6829 65.2152 31.1457 65.1824 31.5522 65.1168C31.9662 65.0494 32.3592 64.9316 32.7193 64.7675C33.0829 64.6018 33.4211 64.3779 33.7283 64.1019C34.1128 63.7485 34.4335 63.3421 34.6809 62.8919C34.9257 62.4433 35.11 61.9342 35.2261 61.3805C35.3423 60.8319 35.4012 60.2167 35.4012 59.5545C35.4003 57.5282 34.8054 55.9916 33.6357 54.9885ZM31.723 62.5931C31.5808 62.7168 31.41 62.8161 31.2147 62.886C31.0102 62.9583 30.8117 63.0046 30.6223 63.0223C30.4229 63.0408 30.141 63.0509 29.7842 63.0509H28.3301V56.1119H29.5696C30.2184 56.1119 30.7746 56.1826 31.224 56.3215C31.6439 56.4494 32.0057 56.7599 32.2994 57.2463C32.6007 57.7436 32.7538 58.5152 32.7538 59.541C32.7538 60.9875 32.408 62.0133 31.723 62.5931Z"
      />
      <path
        fill={fill || '#0F6A88'}
        d="M43.5989 53.9536H38.422C38.077 53.9536 37.7976 54.0049 37.5687 54.1101C37.3239 54.2212 37.1412 54.4021 37.0251 54.6487C36.9174 54.8793 36.8652 55.1612 36.8652 55.5104V63.853C36.8652 64.3554 36.9881 64.7416 37.2279 65.0008C37.4728 65.2667 37.7926 65.4022 38.178 65.4022C38.5558 65.4022 38.873 65.2692 39.1204 65.0058C39.3611 64.7458 39.4831 64.3579 39.4831 63.853V60.4912H42.8895C43.2707 60.4912 43.5694 60.3995 43.7756 60.2169C43.9902 60.0275 44.0987 59.7734 44.0987 59.4604C44.0987 59.1482 43.9927 58.8932 43.7823 58.703C43.5778 58.5162 43.2774 58.422 42.8895 58.422H39.4831V56.0691H43.5997C44.0028 56.0691 44.3133 55.9724 44.5229 55.7805C44.7374 55.5853 44.846 55.3253 44.846 55.008C44.846 54.6975 44.7374 54.44 44.522 54.2414C44.31 54.0495 44.0003 53.9536 43.5989 53.9536Z"
      />
    </svg>
  );
};

const GenericFileIcon: React.FC<React.PropsWithChildren<FileIconInterface>> = ({
  htmlId,
  title,
  className,
  fill,
  opaque,
}) => {
  const titleId = `GenericFileIcon-${htmlId}`;
  return (
    <svg
      className={classNames(css.commonIcon, className)}
      id={title}
      viewBox="0 0 65 82"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-labelledby={titleId}
      role={title ? 'img' : ''}
      aria-hidden={title === ''}
    >
      <title id={titleId}>{title}</title>
      {opaque && <rect width="54" height="70" x={4} y={4} fill="white" />}
      <path
        fill={fill || '#0F6A88'}
        d="M42.9206 0.657227H4.81769C2.48253 0.657227 0.584961 2.54975 0.584961 4.89079V72.1583C0.584961 74.4968 2.48253 76.3919 4.81769 76.3919H55.6213C57.9615 76.3919 59.8541 74.4968 59.8541 72.1583V17.5915L42.9206 0.657227ZM42.9206 6.64279L53.8693 17.5915H42.9206V6.64279ZM55.6222 72.1583H4.81769V4.89079H38.6871V17.5915C38.6871 19.93 40.5804 21.8251 42.9198 21.8251H55.6213V72.1583H55.6222Z"
      />
    </svg>
  );
};

const ArrowBoxLeftSvg: React.FC<
  React.PropsWithChildren<InnerIconInterface>
> = ({ htmlId, title, className }) => {
  const titleId = `ArrowBoxLeft-${htmlId}`;

  return (
    <svg
      className={classNames(css.commonIcon, className)}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title id={titleId}>{title}</title>
      <path d="M3 20.98L21 20.98C22.1 20.98 23 20.08 23 18.98L23 14.99L21 14.99L21 19L3 19L3 4.97L21 4.97L21 8.99L23 8.99L23 4.98C23 3.88 22.1 3 21 3L3 3C1.9 3 0.999999 3.88 0.999999 4.98L1 18.98C1 20.09 1.9 20.98 3 20.98ZM13 7.99L9 11.99L13 15.99L13 12.99L23 12.99L23 10.99L13 10.99L13 7.99ZM3 20.98L21 20.98C22.1 20.98 23 20.08 23 18.98L23 14.99L21 14.99L21 19L3 19L3 4.97L21 4.97L21 8.99L23 8.99L23 4.98C23 3.88 22.1 3 21 3L3 3C1.9 3 0.999999 3.88 0.999999 4.98L1 18.98C1 20.09 1.9 20.98 3 20.98ZM13 7.99L9 11.99L13 15.99L13 12.99L23 12.99L23 10.99L13 10.99L13 7.99Z" />
    </svg>
  );
};

const ArrowBoxRightSvg: React.FC<
  React.PropsWithChildren<InnerIconInterface>
> = ({ htmlId, title, className }) => {
  const titleId = `ArrowBoxRight-${htmlId}`;

  return (
    <svg
      className={classNames(css.commonIcon, className)}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title id={titleId}>{title}</title>
      <path d="M21 3.00001L3 3.00001C1.9 3.00001 1 3.90001 1 5.00001L0.999999 8.99001L3 8.99001L3 4.98001L21 4.98001L21 19.01L3 19.01L3 14.99L0.999998 14.99L0.999997 19C0.999997 20.1 1.9 20.98 3 20.98L21 20.98C22.1 20.98 23 20.1 23 19L23 5.00001C23 3.89001 22.1 3.00001 21 3.00001ZM11 15.99L15 11.99L11 7.99001L11 10.99L0.999999 10.99L0.999998 12.99L11 12.99L11 15.99ZM21 3.00001L3 3.00001C1.9 3.00001 1 3.90001 1 5.00001L0.999999 8.99001L3 8.99001L3 4.98001L21 4.98001L21 19.01L3 19.01L3 14.99L0.999998 14.99L0.999997 19C0.999997 20.1 1.9 20.98 3 20.98L21 20.98C22.1 20.98 23 20.1 23 19L23 5.00001C23 3.89001 22.1 3.00001 21 3.00001ZM11 15.99L15 11.99L11 7.99001L11 10.99L0.999999 10.99L0.999998 12.99L11 12.99L11 15.99Z" />
    </svg>
  );
};

const EvaluationSvg: React.FC<React.PropsWithChildren<InnerIconInterface>> = ({
  htmlId,
  title,
  className,
}) => {
  const titleId = `Evaluation-${htmlId}`;

  return (
    <svg
      className={classNames(css.commonIcon, className)}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title id={titleId}>{title}</title>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.82 3H19C20.1 3 21 3.9 21 5V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V5C3 3.9 3.9 3 5 3H9.18C9.6 1.84 10.7 1 12 1C13.3 1 14.4 1.84 14.82 3ZM13 4C13 3.45 12.55 3 12 3C11.45 3 11 3.45 11 4C11 4.55 11.45 5 12 5C12.55 5 13 4.55 13 4ZM12 7C13.66 7 15 8.34 15 10C15 11.66 13.66 13 12 13C10.34 13 9 11.66 9 10C9 8.34 10.34 7 12 7ZM6 17.6V19H18V17.6C18 15.6 14 14.5 12 14.5C10 14.5 6 15.6 6 17.6Z"
        fill="#B0BDBD"
      />
    </svg>
  );
};

export default Icon;
